import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import SwalTopEnd from "../utils/swal_top_end";
import {
  getMomoStats,
  getVenteMomoByYear,
  importMomoFile,
  importMomoDeleteFile,
} from "../services/momoService";

const REQUIRED_COLUMNS = [
  "MSISDN_NSK",
  "MT_CASH_IN",
  "MT_CASH_OUT",
  "MSISDN_COMMERCIAL",
  "NOM_COMMERCIAL",
];

export default function useMomoDashboard() {
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [dateCapture, setDateCapture] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [excel, setExcel] = useState(null);

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const [TotalApproDay, setTotalApproDay] = useState(0);
  const [TotalCashin, setTotalCashin] = useState(0);
  const [TotalCashout, setTotalCashout] = useState(0);
  const [NbreCashin, setNbreCashin] = useState(0);
  const [NbreCashout, setNbreCashout] = useState(0);

  const [dataSecteurCashin, setDataSecteurCashin] = useState([]);
  const [dataSecteurCashout, setDataSecteurCashout] = useState([]);
  const [recapCommercials, setRecapCommercials] = useState([]);
  const [topPos, setTopPos] = useState([]);
  const [venteSerie, setVenteSerie] = useState([]);

  const resetState = useCallback(() => {
    setTotalApproDay(0);
    setTotalCashin(0);
    setTotalCashout(0);
    setNbreCashin(0);
    setNbreCashout(0);
    setRecapCommercials([]);
    setDataSecteurCashin([]);
    setDataSecteurCashout([]);
    setTopPos([]);
  }, []);

  const fetchMomoStats = useCallback(async (debut, fin) => {
    setLoadingStats(true);
    try {
      const { data } = await getMomoStats(debut, fin);
      setTotalApproDay(data.totalMomo);
      setTotalCashin(data.totalCashin);
      setTotalCashout(data.totalCashout);
      setNbreCashin(data.NbreCashin);
      setNbreCashout(data.NbreCashout);
      setRecapCommercials(data.commercialTab);
      setTopPos(data.posTab);
      const secteurIn = Object.entries(data.maxSecteurEvd).map(([name, value]) => ({ name, value }));
      const secteurOut = Object.entries(data.secteurMomoOuts).map(([name, value]) => ({ name, value }));
      setDataSecteurCashin(secteurIn);
      setDataSecteurCashout(secteurOut);
      window.$("#exampleModalFiltre").modal("hide");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchVenteByYear = useCallback(async () => {
    setVenteSerie([]);
    try {
      const { data } = await getVenteMomoByYear();
      const labels = Object.values(data.labels);
      const transformed = labels.map((month) => ({
        month,
        cashin: data.venteSerieIn[month] || 0,
        cashout: data.venteSerieOut[month] || 0,
      }));
      setVenteSerie(transformed);
    } catch {
      // silent
    }
  }, []);

  const loadDashboard = useCallback(async (debut, fin) => {
    await Promise.all([fetchMomoStats(debut, fin), fetchVenteByYear()]);
  }, [fetchMomoStats, fetchVenteByYear]);

  const handleFile = (e) => {
    const EXCEL_TYPES = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    const file = e.target.files[0];
    if (!file) return;
    if (!EXCEL_TYPES.includes(file.type)) {
      SwalTopEnd({ icon: "error", title: "Sélectionnez uniquement un fichier Excel" });
      setExcelFile(null);
      window.$("#file_excel").val("");
      return;
    }
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (ev) => {
      setExcelFile(ev.target.result);
      setExcel(file);
    };
  };

  const parseAndValidateExcel = () => {
    const workbook = XLSX.read(excelFile, { type: "buffer" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet);
    if (!rows.length) return { rows: null, error: "Le fichier est vide" };
    const keys = Object.keys(rows[0]);
    const missing = REQUIRED_COLUMNS.some((col) => !keys.includes(col));
    if (missing) return { rows: null, error: "Le fichier ne correspond pas au modèle" };
    return { rows, error: null };
  };

  const buildFormData = () => {
    const ts = Math.floor(Date.now() / 1000);
    const ext = excel.name.split(".").pop();
    const renamed = new File([excel], `${ts}_excel_.${ext}`, { type: excel.type });
    const fd = new FormData();
    fd.append("file", renamed);
    fd.append("capture_date", dateCapture);
    return fd;
  };

  const resetImportForm = () => {
    setExcelFile(null);
    setExcel(null);
    setDateCapture("");
    window.$("#file_excel").val("");
    window.$("#datecapture").val("");
    window.$("#exampleModalCenteContrat").modal("hide");
  };

  const doImport = useCallback(async (deleteFirst = false) => {
    setLoadingImport(true);
    const fd = buildFormData();
    try {
      const importFn = deleteFirst ? importMomoDeleteFile : importMomoFile;
      const resp = await importFn(fd);
      setLoadingImport(false);
      if (!deleteFirst && resp.data.code === 405) {
        const result = await Swal.fire({
          title: "Attention! cette donnée existe",
          text: `Cette action va supprimer les données du ${dateCapture}.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Oui, écraser !",
          cancelButtonText: "Non, conserver !",
        });
        if (result.isConfirmed) await doImport(true);
      } else {
        resetImportForm();
        loadDashboard(dateDebut, dateFin);
        Swal.fire({
          title: "Cette opération peut prendre quelques minutes...",
          html: `<b style="color:green">Le fichier est en cours de traitement, un mail vous sera envoyé une fois le traitement terminé</b>`,
          icon: "warning",
        });
      }
    } catch (err) {
      setLoadingImport(false);
      if (err?.response?.status === 500) {
        SwalTopEnd({ icon: "error", title: "Une erreur est survenue" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excel, excelFile, dateCapture, dateDebut, dateFin]);

  const submitImport = async (e) => {
    e.preventDefault();
    if (!dateCapture) {
      SwalTopEnd({ icon: "error", title: "La date est obligatoire" });
      return;
    }
    if (!excel) return;
    const { rows, error } = parseAndValidateExcel();
    if (error) {
      SwalTopEnd({ icon: "error", title: error });
      return;
    }
    if (!rows) return;
    await doImport(false);
  };

  return {
    dateDebut, setDateDebut,
    dateFin, setDateFin,
    dateCapture, setDateCapture,
    loadingStats, loadingImport,
    TotalApproDay, TotalCashin, TotalCashout, NbreCashin, NbreCashout,
    dataSecteurCashin, dataSecteurCashout,
    recapCommercials, topPos, venteSerie,
    loadDashboard, resetState,
    handleFile, submitImport,
  };
}
